<template>
  <table-wrapper
    v-model:page-options="pageOptions"
    v-model:filters="filters"
    :fields="tableFields"
    :filter-configuration="tableFilters"
    :items="checkBotConfigs"
    :loading="isLoading"
    :mode="TableMode.CLIENT_SIDE"
    row-clickable
    @row-click="handleTableRowClick"
  >
  </table-wrapper>
</template>

<script setup lang="ts">
  import { ref, computed, onMounted } from 'vue';
  import { useRouter } from 'vue-router';
  import { useI18n } from 'vue-i18n';
  import { useToast } from '@/composables/useToast';
  import { ICheckBotConfigSummaryDto, ProjectType } from 'types';
  import { CheckBotConfigsService } from '@/services';
  import {
    TableFieldsBuilder,
    TableFiltersBuilder,
    TableMode,
    useTableWrapper,
  } from '../TableWrapper';
  import { Route, routeParams } from '@/routes';
  import { PROJECT_TYPE_PARAM } from '@/routes';

  const columnWidths = {
    name: '30%',
    description: '50%',
    checksCount: '20%',
  };

  const props = defineProps<{
    projectId: string;
    supplierId?: string;
  }>();

  const checkBotConfigs = ref<ICheckBotConfigSummaryDto[]>([]);

  const { t } = useI18n();
  const { createErrorToast } = useToast();
  const router = useRouter();

  const TABLE_NAMESPACE = 'checkBotConfigsTable';
  const { pageOptions, filters, isLoading } =
    useTableWrapper<ICheckBotConfigSummaryDto>(
      TableMode.CLIENT_SIDE,
      'name',
      getCheckBotConfigs,
      undefined,
      true,
      TABLE_NAMESPACE,
    );

  const tableFields = computed(() =>
    new TableFieldsBuilder<ICheckBotConfigSummaryDto>({
      labelTranslationKey: 'checkBotConfigsTable',
      sortable: true,
    })
      .addField('name', { width: columnWidths.name })
      .addField('description', { width: columnWidths.description })
      .addField('checksCount', { width: columnWidths.checksCount })
      .build(),
  );

  const tableFilters = computed(() =>
    new TableFiltersBuilder<ICheckBotConfigSummaryDto>({
      translationKey: 'checkBotConfigsTable',
    })
      .addInputFilter('name', { width: '200px' })
      .addInputFilter('description', { width: '250px' })
      .addInputFilter('checksCount', { width: '200px' })
      .build(),
  );

  async function getCheckBotConfigs() {
    try {
      const { configs } = await CheckBotConfigsService.getCheckBotConfigs(
        props.projectId,
        props.supplierId,
      );
      checkBotConfigs.value = configs;
    } catch (error) {
      createErrorToast(
        t('checkBotConfigsTable.errors.errorGettingCheckBotConfigs'),
      );
    }
  }

  async function handleTableRowClick(config: ICheckBotConfigSummaryDto) {
    router.push({
      name: Route.CHECK_BOT_CONFIGURATOR,
      params: {
        [routeParams[Route.CHECK_BOT_CONFIGURATOR].projectId]: props.projectId,
      },
      query: {
        [routeParams[Route.CHECK_BOT_CONFIGURATOR].query.configId]: config._id,
        [routeParams[Route.CHECK_BOT_CONFIGURATOR].query.supplierId]:
          props.supplierId,
        [routeParams[Route.CHECK_BOT_CONFIGURATOR].query[PROJECT_TYPE_PARAM]]:
          props.supplierId
            ? ProjectType.SUPPLIER_PROJECT
            : ProjectType.DIGITALIZATION_PROJECT,
      },
    });
  }

  onMounted(() => {
    getCheckBotConfigs();
  });
</script>
